// CookSettingsDrawer — the side tab + hidden drawer for cook-screen settings
// (README §6.7 timed screens).
//
// A vertical pull-tab is fixed to the screen edge (the handedness side),
// vertically centred. Pressing it (a disclosure) slides out a drawer holding
// the cook-screen configuration: screen colour (inline swatch grid with a
// hovered/focused name caption), speech speed, voice persona, and the
// activation-cue ("chime") selector with preview. The quick toggles (mute,
// handedness) stay in the top bar; configuration lives here.
//
// Rendered ONCE per cooking screen (LiveCookPane / PrepPane) — a single fixed
// tab, not one per rail. Reads/writes the session store directly. The drawer
// closes on the tab, the ✕, Escape, or an outside click.

import { useEffect, useRef, useState } from 'react';
import { SCHEME_LABELS, SCHEME_ORDER, theme, type SchemeName } from '../design/theme';
import { palettes } from '../design/tokens';
import { useAppStore } from '../state/store';
import { useVoices } from './useVoices';
import { resolvePersona, VOICE_PERSONAS, type VoicePersona } from './voicePersonas';
import { CUE_SOUNDS, previewCue, previewSpeech, type CueSound } from './sounds';

// The fixed audition line for the speed / voice previews.
const SPEECH_PREVIEW = 'Yes chef, go chef, three bags full chef.';

const SPEEDS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 0.5, label: 'x0.5' },
  { value: 0.75, label: 'x0.75' },
  { value: 1.0, label: 'x1.0' },
  { value: 1.25, label: 'x1.25' },
  { value: 1.5, label: 'x1.5' },
  { value: 1.75, label: 'x1.75' },
];

function CogIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" style={{ display: 'block' }}>
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM19.4 13a7.6 7.6 0 0 0 0-2l1.7-1.3-1.7-3-2 .8a7.6 7.6 0 0 0-1.7-1l-.3-2.1h-3.4l-.3 2.1a7.6 7.6 0 0 0-1.7 1l-2-.8-1.7 3L5.6 11a7.6 7.6 0 0 0 0 2l-1.7 1.3 1.7 3 2-.8a7.6 7.6 0 0 0 1.7 1l.3 2.1h3.4l.3-2.1a7.6 7.6 0 0 0 1.7-1l2 .8 1.7-3z"
      />
    </svg>
  );
}

export function CookSettingsDrawer() {
  const [open, setOpen] = useState(false);
  // The scheme whose name the caption shows; null → fall back to the selected.
  const [hoveredScheme, setHoveredScheme] = useState<SchemeName | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const tabRef = useRef<HTMLButtonElement>(null);

  const handedness = useAppStore((s) => s.handedness);
  const colorScheme = useAppStore((s) => s.colorScheme);
  const setColorScheme = useAppStore((s) => s.setColorScheme);
  const speechRate = useAppStore((s) => s.speechRate);
  const setSpeechRate = useAppStore((s) => s.setSpeechRate);
  const voicePersona = useAppStore((s) => s.voicePersona);
  const setVoicePersona = useAppStore((s) => s.setVoicePersona);
  const cueSound = useAppStore((s) => s.cueSound);
  const setCueSound = useAppStore((s) => s.setCueSound);
  // Warm the voice list so the persona resolves to a real voice promptly.
  const voices = useVoices();

  const edge = handedness === 'right' ? 'right' : 'left';

  const close = (): void => {
    setOpen(false);
    tabRef.current?.focus();
  };

  // Dismiss on Escape or an outside click while open (mirrors ColourToggle).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  // Auto-preview the cue the moment it's chosen (preview ignores mute).
  const handleCue = (cue: CueSound): void => {
    setCueSound(cue);
    previewCue(cue);
  };

  // Audition the spoken voice at the chosen speed + persona (▶ on the Speed
  // and Voice rows). Same phrase from either button.
  const handleSpeechPreview = (): void => {
    const { voice, pitch } = resolvePersona(voicePersona, voices);
    previewSpeech(SPEECH_PREVIEW, { rate: speechRate, pitch, voice });
  };

  const labelStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.2rem',
    fontSize: '0.7rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.text.muted,
  };
  const selectStyle: React.CSSProperties = {
    fontSize: '0.85rem',
    padding: '0.25rem',
    borderRadius: '8px',
    border: `1px solid ${theme.bg.muted}`,
    background: theme.bg.base,
    color: theme.text.primary,
    cursor: 'pointer',
  };
  const previewBtnStyle: React.CSSProperties = {
    width: '36px',
    height: '32px',
    flex: '0 0 auto',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.accent.action,
    color: theme.surface.onAccent,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  };

  const captionScheme = hoveredScheme ?? colorScheme;

  return (
    <div ref={ref}>
      {/* Vertical pull-tab fixed to the handedness edge, vertically centred. */}
      <button
        ref={tabRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Cooking settings"
        onClick={() => setOpen((o) => !o)}
        style={{
          position: 'fixed',
          top: '50%',
          transform: 'translateY(-50%)',
          [edge]: 0,
          width: '30px',
          height: '76px',
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.25rem',
          background: open ? theme.accent.calm : theme.bg.raised,
          color: open ? theme.bg.base : theme.text.muted,
          // Longhand borders only (no shorthand) so dropping the edge-facing
          // side doesn't trip React's shorthand/longhand warning.
          borderTop: `1px solid ${theme.bg.muted}`,
          borderBottom: `1px solid ${theme.bg.muted}`,
          borderLeft: edge === 'right' ? `1px solid ${theme.bg.muted}` : 'none',
          borderRight: edge === 'left' ? `1px solid ${theme.bg.muted}` : 'none',
          // Rounded on the inner corners only, so it looks attached to the edge.
          borderRadius: edge === 'right' ? '10px 0 0 10px' : '0 10px 10px 0',
          cursor: 'pointer',
          zIndex: 30,
          boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
        }}
      >
        <CogIcon />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Cooking settings"
          style={{
            position: 'fixed',
            top: '50%',
            transform: 'translateY(-50%)',
            // Sit just inside the tab on the same edge.
            [edge]: '30px',
            width: '248px',
            maxHeight: '88vh',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.9rem',
            padding: '0.9rem',
            background: theme.bg.raised,
            color: theme.text.primary,
            border: `1px solid ${theme.bg.muted}`,
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            zIndex: 30,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontWeight: 700,
              fontSize: '0.95rem',
            }}
          >
            Settings
            <button
              type="button"
              aria-label="Close settings"
              onClick={close}
              style={{
                width: '32px',
                height: '32px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                color: theme.text.muted,
                cursor: 'pointer',
                fontSize: '1.1rem',
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>

          {/* Screen colour — inline swatch grid with a hovered/focused caption. */}
          <div style={labelStyle}>
            Colour
            <div
              role="radiogroup"
              aria-label="Screen colour"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '0.4rem',
                marginTop: '0.15rem',
              }}
            >
              {SCHEME_ORDER.map((scheme: SchemeName) => {
                const selected = scheme === colorScheme;
                return (
                  <button
                    key={scheme}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={SCHEME_LABELS[scheme]}
                    title={SCHEME_LABELS[scheme]}
                    onClick={() => setColorScheme(scheme)}
                    onMouseEnter={() => setHoveredScheme(scheme)}
                    onMouseLeave={() => setHoveredScheme(null)}
                    onFocus={() => setHoveredScheme(scheme)}
                    onBlur={() => setHoveredScheme(null)}
                    style={{
                      height: '30px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: palettes[scheme].bg.base,
                      border: selected
                        ? `2px solid ${theme.text.primary}`
                        : `1px solid ${theme.bg.muted}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: '14px',
                        height: '14px',
                        borderRadius: '50%',
                        background: palettes[scheme].accent.action,
                      }}
                    />
                  </button>
                );
              })}
            </div>
            {/* Hovered/focused scheme name — visual aid; the radios announce
                their own names, so this is aria-hidden to avoid doubling up. */}
            <span
              aria-hidden="true"
              style={{
                marginTop: '0.3rem',
                fontSize: '0.8rem',
                textTransform: 'none',
                letterSpacing: 'normal',
                fontWeight: 600,
                color: theme.text.primary,
              }}
            >
              {SCHEME_LABELS[captionScheme]}
            </span>
          </div>

          {/* Speech speed + voice preview. */}
          <label style={labelStyle}>
            Audio speed
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <select
                aria-label="Speech speed"
                value={speechRate}
                onChange={(e) => setSpeechRate(parseFloat(e.target.value))}
                style={{ ...selectStyle, flex: 1 }}
              >
                {SPEEDS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                aria-label="Preview speech speed"
                onClick={handleSpeechPreview}
                style={previewBtnStyle}
              >
                ▶
              </button>
            </div>
          </label>

          {/* Voice persona + preview. */}
          <label style={labelStyle}>
            Voice
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <select
                aria-label="Voice"
                value={voicePersona}
                onChange={(e) => setVoicePersona(e.target.value as VoicePersona)}
                style={{ ...selectStyle, flex: 1 }}
              >
                {VOICE_PERSONAS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                aria-label="Preview voice"
                onClick={handleSpeechPreview}
                style={previewBtnStyle}
              >
                ▶
              </button>
            </div>
          </label>

          {/* Activation cue ("chime") + preview. */}
          <label style={labelStyle}>
            Chime
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <select
                aria-label="Chime"
                value={cueSound}
                onChange={(e) => handleCue(e.target.value as CueSound)}
                style={{ ...selectStyle, flex: 1 }}
              >
                {CUE_SOUNDS.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                aria-label="Preview chime"
                onClick={() => previewCue(cueSound)}
                style={previewBtnStyle}
              >
                ▶
              </button>
            </div>
          </label>
        </div>
      )}
    </div>
  );
}

export default CookSettingsDrawer;
